import React from "react";
import {
  Box,
  Typography,
  Chip,
  alpha,
  Icon,
  IconButton,
  Tooltip,
} from "@mui/material";
import { TableColumn } from "../../../../app.interface";
import { formatCurrency, getSourceModuleColor } from "../../utils";
import { IdDisplay } from "../../components/IdDisplay";
import { DateDisplay } from "../../components/DateDisplay";
import { IJournalDetailsData } from "../../accounting.interface";

interface CreateLedgerColumnsProps {
  onJournalEntryClick: (entry: IJournalDetailsData) => void;
  onCopyJournalId: (journalId: string) => void;
}

// Helper function to get status color
const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "posted":
      return "#10B981"; // Green
    case "draft":
      return "#F59E0B"; // Amber
    case "reversed":
      return "#EF4444"; // Red
    default:
      return "#6B7280"; // Gray
  }
};

// Helper function to get journal type from tags
const getJournalType = (tags: any[] = []) => {
  const journalTypeTag = tags.find(
    (tag: any) => tag.tag_type === "JOURNAL_TYPE"
  );
  return journalTypeTag?.tag_value || "Unknown";
};

// Helper function to check if entry is a reversal
const isReversalEntry = (tags: any[] = []) => {
  return tags.some((tag: any) => tag.tag_type === "REVERSAL");
};

export const createLedgerColumns = ({
  onJournalEntryClick,
  onCopyJournalId,
}: CreateLedgerColumnsProps): TableColumn[] => [
  {
    id: "date",
    header: "Date",
    field: "entry_date",
    type: "action",
    width: "120px",
    sortable: true,
    renderAction: (row) => (
      <DateDisplay timestamp={row.entry_date || row.date} showTooltip={true} />
    ),
  },
  {
    id: "journalNumber",
    header: "Journal #",
    field: "journal_entry_number",
    type: "action",
    width: "140px",
    renderAction: (row) => {
      if (row.isOpeningBalance) {
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontWeight: 600,
              color: "info.main",
              py: 0.5,
              minHeight: 40,
              display: "flex",
              alignItems: "center",
            }}
          >
            {row.journal_entry_number || row.journalEntryId}
          </Typography>
        );
      }

      return (
        <IdDisplay
          id={row.journal_entry_number || row.journalEntryId}
          onClick={() => onJournalEntryClick(row.originalEntry || row)}
          onCopy={() => onCopyJournalId(row._id || row.id)}
          label="Copy Journal ID"
        />
      );
    },
  },
  {
    id: "status",
    header: "Status",
    field: "status",
    type: "action",
    width: "100px",
    renderAction: (row) => (
      <Chip
        size="small"
        label={row.status?.toUpperCase() || "UNKNOWN"}
        sx={{
          bgcolor: alpha(getStatusColor(row.status || "unknown"), 0.1),
          color: getStatusColor(row.status || "unknown"),
          fontWeight: 600,
          fontSize: "0.7rem",
          minWidth: 60,
        }}
      />
    ),
  },
  {
    id: "type",
    header: "Type",
    field: "journal_type",
    type: "action",
    width: "120px",
    renderAction: (row) => {
      const journalType = getJournalType(row.tags);
      const isReversal = isReversalEntry(row.tags);

      return (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          <Chip
            size="small"
            label={journalType}
            sx={{
              bgcolor: alpha("#8B5CF6", 0.1),
              color: "#8B5CF6",
              fontSize: "0.7rem",
              textTransform: "capitalize",
            }}
          />
          {isReversal && (
            <Chip
              size="small"
              label="REV"
              sx={{
                bgcolor: alpha("#EF4444", 0.1),
                color: "#EF4444",
                fontSize: "0.65rem",
                fontWeight: 700,
              }}
            />
          )}
        </Box>
      );
    },
  },
  {
    id: "narration",
    header: "Narration",
    field: "narration",
    type: "action",
    width: "250px",
    renderAction: (row) => (
      <Typography
        variant="body2"
        sx={{
          maxWidth: 240,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={row.narration}
      >
        {row.narration || "—"}
      </Typography>
    ),
  },
  {
    id: "source",
    header: "Source",
    field: "source_module",
    type: "action",
    width: "140px",
    renderAction: (row) => {
      const sourceModule = row.source_module || row.sourceModule;

      return (
        <Chip
          size="small"
          label={sourceModule?.replace(/_/g, " ") || "Unknown"}
          sx={{
            bgcolor: alpha(getSourceModuleColor(sourceModule), 0.1),
            color: getSourceModuleColor(sourceModule),
            fontSize: "0.7rem",
            textTransform: "capitalize",
          }}
        />
      );
    },
  },
  {
    id: "branch",
    header: "Branch",
    field: "branch_name",
    type: "action",
    width: "140px",
    renderAction: (row) => (
      <Typography
        variant="body2"
        sx={{
          maxWidth: 130,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: 500,
        }}
        title={row.branch_name}
      >
        {row.branch_name || "—"}
      </Typography>
    ),
  },
  {
    id: "amount",
    header: "Amount",
    field: "total_amount",
    type: "action",
    width: "140px",
    align: "right",
    renderAction: (row) => (
      <Box sx={{ textAlign: "right" }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          {row.currency || "BHD"} {formatCurrency(row.total_amount || 0)}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            fontSize: "0.75rem",
          }}
        >
          {row.journal_lines && row.journal_lines.length > 0 && (
            <>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  color: "success.main",
                  fontWeight: 500,
                }}
              >
                D:{" "}
                {formatCurrency(
                  row.journal_lines.reduce(
                    (sum: number, line: any) => sum + (line.debit || 0),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  color: "error.main",
                  fontWeight: 500,
                }}
              >
                C:{" "}
                {formatCurrency(
                  row.journal_lines.reduce(
                    (sum: number, line: any) => sum + (line.credit || 0),
                    0
                  )
                )}
              </Typography>
            </>
          )}
        </Box>
      </Box>
    ),
  },
  {
    id: "createdBy",
    header: "Created By",
    field: "created_by_email",
    type: "action",
    width: "160px",
    renderAction: (row) => (
      <Box>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            maxWidth: 150,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={row.created_by_email}
        >
          {row.created_by_email || "—"}
        </Typography>
        {row.posted_at && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.7rem" }}
          >
            <DateDisplay timestamp={row.posted_at} format="MMM DD" />
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: "actions",
    header: "Actions",
    field: "actions",
    type: "action",
    width: "100px",
    align: "center",
    renderAction: (row) => (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {!row.isOpeningBalance && (
          <>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onJournalEntryClick(row.originalEntry || row);
                }}
                sx={{
                  bgcolor: alpha("#3B82F6", 0.1),
                  color: "#3B82F6",
                  "&:hover": {
                    bgcolor: alpha("#3B82F6", 0.2),
                  },
                }}
              >
                <Icon sx={{ fontSize: 16 }}>visibility</Icon>
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Journal ID">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyJournalId(row._id || row.id);
                }}
                sx={{
                  bgcolor: alpha("#8B5CF6", 0.1),
                  color: "#8B5CF6",
                  "&:hover": {
                    bgcolor: alpha("#8B5CF6", 0.2),
                  },
                }}
              >
                <Icon sx={{ fontSize: 16 }}>content_copy</Icon>
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    ),
  },
];
